import { Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Timespan } from "@helpers/const/timespan";
import {
	API_ETC_TYPE,
	API_ETF_TYPE,
	API_METAL_TYPE,
	API_STOCK_TYPE,
	EXCLUDED_ASSET_SYMBOLS,
	PRICES_SCROLL_PER_PAGE
} from "@helpers/constants";
import { Dictionary } from "@helpers/types";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { PricesAsset } from "@main-services/prices-scroll.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Fiat } from "@models";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { BpOption } from "@models/ui-components/bp-option";
import { TradablesFacade } from "@modules/+new/core/tradables/domain/+state/tradables.facade";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";
import { AccountService } from "@modules/account/services/account.service";
import { ButtonAction } from "@modules/discover/constants/actions";
import { API_SORT_OPTIONS } from "@modules/discover/constants/constants";
import { ActionButtonConfig, ActionButtonsFacade } from "@modules/discover/domain/+state/action-buttons.facade";
import { PricesInfiniteScrollFacade } from "@modules/discover/domain/+state/prices-infinite-scroll.facade";
import { PricesModel, PricesStateService } from "@modules/discover/domain/+state/prices.state.service";
import { MarginTradingDiscoveryFacade } from "@modules/margin-trading/services/margin-trading-discovery.facade";
import { MarginTradingFacade } from "@modules/margin-trading/services/margin-trading.facade";
import { PRICE_PAGE_LIST_FILTERS } from "@modules/trade/constants/constants";
import { WatchlistFacade } from "@modules/watchlist/domain/+state/watchlist.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, Observable, switchMap } from "rxjs";
import { debounceTime, filter, map, skip, take, withLatestFrom } from "rxjs/operators";
import * as Util from "../../util/prices.util";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";

export interface PricesViewModel extends PricesModel {
	defaultFiat: Fiat;
	filterList: BpOption[];
	isWatchlistAsset: Dictionary<boolean>;
}

@UntilDestroy()
@Injectable()
export class PricesFacade {
	public constructor(
		private readonly pricesStateService: PricesStateService,
		private readonly pricesInfiniteScrollFacade: PricesInfiniteScrollFacade,
		private readonly accountService: AccountService,
		private readonly tradablesFacade: TradablesFacade,
		private readonly tradableListService: TradableListService,
		private readonly tradableWalletListService: TradableWalletListService,
		private readonly walletBalanceFacade: WalletsBalancesFacadeService,
		private readonly actionButtonsFacade: ActionButtonsFacade,
		private readonly watchlistFacade: WatchlistFacade,
		private readonly route: ActivatedRoute,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly statsigService: StatsigClientService,
		private readonly depositCryprocoinsState: CryptocoinsState,
		private readonly marginTradingFacade: MarginTradingFacade,
		private readonly marginTradingDiscoveryFacade: MarginTradingDiscoveryFacade
	) {
		this.initListUpdates();
		this.initSocketPriceUpdates();
		this.marginTradingFacade
			.marginTradingEnabled$()
			.pipe(
				filter((mtEnabled) => mtEnabled),
				take(1),
				switchMap(() => this.marginTradingDiscoveryFacade.getMarginAssets(Timespan.Day))
			)
			.subscribe();
	}

	public getPricesState$(): Observable<PricesModel> {
		return this.pricesStateService.getState$();
	}

	public resetTradablesIds(): void {
		this.pricesStateService.resetTradablesIds();
	}

	public getPricesViewModel$(): Observable<PricesViewModel> {
		return combineLatest([this.getPricesState$(), this.watchlistFacade.getWatchlistState$()]).pipe(
			// filter out initial emit
			filter(([pricesModel]) => !!pricesModel && !!pricesModel.tradableIds),
			map(([pricesModel, watchlistState]) => {
				const defaultFiat = this.accountService.getDefaultFiat();

				let filterList = PRICE_PAGE_LIST_FILTERS;

				const isWatchlistAsset = watchlistState?.tradableIds.reduce((acc, tId) => {
					acc[tId] = watchlistState.tradableIds.includes(tId);
					return acc;
				}, {});

				return {
					...pricesModel,
					defaultFiat,
					filterList,
					isWatchlistAsset
				};
			})
		);
	}

	public getPricesModel$(): Observable<PricesModel> {
		return combineLatest([
			// first emit by BehaviorSubject's default value should be ignored (cannot filter by [], as that value is valid)
			this.pricesInfiniteScrollFacade.getList$().pipe(skip(1)),
			this.walletBalanceFacade.getCryptoBalancesStatus$(),
			this.launchDarklyService.isActive$(LD_FLAG.DEPOSIT_MULTICHAIN),
			this.depositCryprocoinsState.availableCryptoDepositList$,
			this.marginTradingFacade.marginTradingEnabled$(),
			this.statsigService.isFeatureActive(FEAT_GATES.MARGIN_TRADING_BUY)
		]).pipe(
			debounceTime(0), // https://blog.strongbrew.io/combine-latest-glitch/
			map(
				([
					pricesAssets,
					walletBalances,
					isMultichainEnabled,
					availableDepositCryptocoins,
					marginTradingEnabled,
					marginTradingBuyEnabled
				]) => {
					const defaultFiat = this.accountService.getDefaultFiat();

					const filteredPriceForUKUsers = pricesAssets.filter((asset) => {
						return (
							!EXCLUDED_ASSET_SYMBOLS.includes(asset.symbol) &&
							asset.group !== API_STOCK_TYPE &&
							asset.group !== API_METAL_TYPE &&
							asset.group !== API_ETC_TYPE &&
							asset.group !== API_ETF_TYPE
						);
					});

					return this.getPricesModel(
						filteredPriceForUKUsers,
						defaultFiat,
						walletBalances,
						isMultichainEnabled,
						availableDepositCryptocoins,
						marginTradingEnabled,
						marginTradingBuyEnabled
					);
				}
			)
		);
	}

	public updateList(
		sortColumn: string,
		sortDir: string,
		timeframe: Timespan,
		filter: string,
		search: string,
		tags: number[]
	): void {
		this.pricesInfiniteScrollFacade.updateList(sortColumn, sortDir, timeframe, filter, search, tags);
	}

	private initListUpdates(): void {
		this.getPricesModel$()
			.pipe(untilDestroyed(this))
			.subscribe((model: PricesModel) => {
				// WARNING: HACKY CODE
				if (model.tradableIds.length > PRICES_SCROLL_PER_PAGE) {
					// length is only > 20 if we are on page 2 or more;
					// when we only load a new page, we don't want to override existing prices by sockets,
					// so we just add prices and keep existing ones
					this.pricesStateService.updateStateButOnlyAddPrices(model);
				} else {
					// if it's the first page (amount of items <= 20), then we override all existing prices
					this.pricesStateService.updateState(model);
				}
			});
	}

	private initSocketPriceUpdates(): void {
		this.tradablesFacade.tradables$
			.pipe(
				skip(1), // skip first emit to not update prices immediately after load
				filter(() => this.route.snapshot.queryParams["sort"] !== API_SORT_OPTIONS.CHANGE), // don't update prices when sorting is set to "change"
				withLatestFrom(this.pricesStateService.getState$()),
				filter(([tradables, state]) => !!tradables && !!state && state.tradableIds?.length > 0),
				untilDestroyed(this)
			)
			.subscribe(([tradables, state]) => {
				const defaultFiat = this.accountService.getDefaultFiat();
				const price: Dictionary<number> = {};
				const marketCap: Dictionary<number> = {};
				const changePercent: Dictionary<Dictionary<number>> = {};

				state.tradableIds.forEach((tId) => {
					const tradable = tradables[tId];
					price[tId] = tradable.assetPrice.price;
					marketCap[tId] = Util.getMarketCapNew(tradable, defaultFiat);

					// we don't get 6m and 5y socket updates, so we should use what we get from prices API
					changePercent[tId] = {
						...Util.getChangePercent(tradable.assetPrice.pricePercentageChanges),
						[Timespan.SixMonths]: state.changePercent[tId][Timespan.SixMonths],
						[Timespan.FiveYears]: state.changePercent[tId][Timespan.FiveYears]
					};
				});

				this.pricesStateService.updatePriceData(price, changePercent, marketCap);
			});
	}

	private getPricesModel(
		pricesAssets: PricesAsset[],
		fiat: Fiat,
		walletBalances: WalletsBalanceStatus,
		isMultichainEnabled: boolean,
		availableDespoitCryptocoins: PaymentsCryptocoin[],
		marginTradingEnabled: boolean = false,
		marginTradingBuyEnabled: boolean = false
	): PricesModel {
		const tradableIds: string[] = [];
		const oldTradables: Dictionary<Tradable> = {};
		const name: Dictionary<string> = {};
		const logo: Dictionary<string> = {};
		const defaultLogo: Dictionary<string> = {};
		const symbol: Dictionary<string> = {};
		const price: Dictionary<number> = {};
		const marketCap: Dictionary<number> = {};
		const changePercent: Dictionary<Dictionary<number>> = {};
		const chartColor: Dictionary<Dictionary<string>> = {};
		const isIndex: Dictionary<boolean> = {};
		const priceFiatPrecision: Dictionary<number> = {};
		const actionButtonBuy: Dictionary<ActionButtonConfig> = {};
		const actionButtonsMore: Dictionary<ActionButtonConfig[]> = {};

		pricesAssets.forEach((t) => {
			const oldTradable = this.tradableListService.getItemByPid(t.pid);

			// there was a case where we received an asset via prices endpoint and it didn't exist on e.g.
			// /assets/prices and /assets/setting - this condition will prevent errors in that case
			if (!oldTradable) {
				return;
			}

			const tId = oldTradable.getId(); // TODO: change to PID at some point
			oldTradables[tId] = oldTradable;
			tradableIds.push(tId);

			isIndex[tId] = Util.isIndex(oldTradable);
			price[tId] = Util.getPrice(t);
			marketCap[tId] = +Util.getMarketCap(t.price_data.market_cap, isIndex[tId], fiat);
			changePercent[tId] = Util.getChangePercent(t.price_data.change_percent);
			name[tId] = t.name;
			logo[tId] = oldTradable.getLogo();
			defaultLogo[tId] = oldTradable.getDefaultLogo();
			symbol[tId] = oldTradable.getSymbol(false, true);
			chartColor[tId] = Util.getChartColorClassesNew(t);
			priceFiatPrecision[tId] = Math.max(2, oldTradable.getPrecisionFiat());
			const depositCrypto = availableDespoitCryptocoins.find((it) => it.cryptocoin_id.toString() === tId);

			const actionButtons = this.getActionButtonConfigs(
				oldTradable,
				walletBalances,
				fiat,
				isMultichainEnabled,
				depositCrypto,
				false,
				marginTradingEnabled &&
					this.marginTradingDiscoveryFacade.isTradableMarginAsset(oldTradable) &&
					oldTradable.getAssetSettings().isBuyActive(),
				marginTradingBuyEnabled
			);

			actionButtonBuy[tId] = actionButtons.find((a) => a.action === ButtonAction.BUY);
			actionButtonsMore[tId] = actionButtons.filter((a) => a.action !== ButtonAction.BUY);
		});

		return {
			tradableIds,
			name,
			logo,
			defaultLogo,
			symbol,
			price,
			marketCap,
			changePercent,
			chartColor,
			isIndex,
			priceFiatPrecision,
			actionButtonBuy,
			actionButtonsMore,
			oldTradables
		};
	}

	public getActionButtonConfigs(
		tradable: Tradable,
		walletBalances,
		defaultFiat: Fiat,
		isMultichainEnabled: boolean,
		depositTradable: PaymentsCryptocoin,
		hasCancelOrder = false,
		marginTradingEnabled = false,
		marginTradingBuyEnabled: boolean = false
	): ActionButtonConfig[] {
		const highestBalanceWallet: TradableWallet = this.tradableWalletListService.getHighestBalanceWallet(
			tradable.getId()
		);
		const defaultWallet = this.tradableWalletListService
			.getWalletsByTradableId(tradable?.getId())
			?.find((wallet) => wallet.isDefault());

		return this.actionButtonsFacade.getActionButtonConfigs(
			tradable,
			walletBalances,
			defaultFiat,
			highestBalanceWallet,
			defaultWallet,
			isMultichainEnabled,
			depositTradable,
			hasCancelOrder,
			marginTradingEnabled,
			marginTradingBuyEnabled
		);
	}

	public getIdFromPid(pid: string): string {
		return this.tradableListService.getItemByPid(pid)?.getId();
	}
}
